import {
  CPF_LENGTH,
  FIRST_CPF_VERIFICATION_DIGIT_INDEX,
  MAX_RG_LENGTH,
  MIN_CNPJ_LENGTH,
  MIN_RG_LENGTH,
  NUM_DV,
  POSITION_DV_CALCULATION_START,
  RG_REGEX,
  SECOND_CPF_VERIFICATION_DIGIT_INDEX,
} from "@/constants/documents.constants";
import { DocumentType } from "@/types/document.types";

import { checkIfIsAllSameDigit } from "./string-helpers";

export function validateCPF(cpf: string): boolean {
  cpf = cpf.replace(/\D/g, "");
  if (cpf.toString().length != CPF_LENGTH || /^(\d)\1{10}$/.test(cpf)) return false;
  let isValid = true;
  [FIRST_CPF_VERIFICATION_DIGIT_INDEX, SECOND_CPF_VERIFICATION_DIGIT_INDEX].forEach(function (j) {
    let sum = 0,
      result;
    cpf
      .split(/(?=)/)
      .splice(0, j)
      .forEach(function (e, i) {
        sum += parseInt(e) * (j + 2 - (i + 1));
      });
    result = sum % CPF_LENGTH;
    result = result < 2 ? 0 : CPF_LENGTH - result;
    if (result.toString() != cpf.substring(j, j + 1)) isValid = false;
  });
  return isValid;
}

function formatRG(rg: string): string {
  return rg.replace(/[.-]/g, "").replace("_", "").trim().toUpperCase();
}

export function validateRG(rg: string) {
  rg = formatRG(rg);
  const rgLenght = rg.length;
  const isTooShort = rgLenght < MIN_RG_LENGTH;
  const isTooLong = rgLenght > MAX_RG_LENGTH;
  if (!RG_REGEX.test(rg)) {
    return false;
  }
  if (isTooLong || isTooShort) return false;

  return true;
}

export function validateCNPJ(cnpj: string) {
  cnpj = cnpj.replace(/[^\d]+/g, "");

  if (cnpj.length !== MIN_CNPJ_LENGTH) return false;

  const isAllSameDigit = checkIfIsAllSameDigit(cnpj);

  if (isAllSameDigit) return false;

  let numbersSize: number = cnpj.length - NUM_DV;
  let numbers: string = cnpj.substring(0, numbersSize);
  const verificationDigits: string = cnpj.substring(numbersSize);

  let sum: number = 0;
  let position: number = numbersSize - POSITION_DV_CALCULATION_START;

  for (let i = numbersSize; i >= 1; i--) {
    const index = numbersSize - i;
    sum += Number(numbers.charAt(index)) * position--;
    if (position < 2) position = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (result !== parseInt(verificationDigits.charAt(0))) return false;

  numbersSize = numbersSize + 1;
  numbers = cnpj.substring(0, numbersSize);
  sum = 0;
  position = numbersSize - 7;

  for (let i = numbersSize; i >= 1; i--) {
    const index = numbersSize - i;
    sum += Number(numbers.charAt(index)) * position--;
    if (position < 2) position = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (result !== parseInt(verificationDigits.charAt(1))) return false;

  return true;
}

export const DOCUMENT_MASK = (documentType: DocumentType): string => {
  let mask = "";
  switch (documentType) {
    case DocumentType.CPF:
      mask = "999.999.999-99";
      break;
    case DocumentType.CNPJ:
      mask = "99.999.999/9999-99";
      break;
    case DocumentType.RG:
      mask = "99.999.999-*";
      break;
    case DocumentType.UNDEFINED:
      mask = "";
      break;
  }
  return mask;
};

export const determineDocumentType = (document: string): DocumentType => {
  if (!document) return DocumentType.UNDEFINED;
  const digits = document.replace(/\D/g, "");

  if (digits.length === 11) {
    if (validateCPF(digits)) {
      return DocumentType.CPF;
    }
  } else if (digits.length >= 12 && digits.length <= 14) {
    if (validateCNPJ(digits)) {
      return DocumentType.CNPJ;
    }
  }

  return DocumentType.UNDEFINED;
};

export const getDocumentMaskByLength = (digits: string): string => {
  const digitsLength = digits.replace(/\D/g, "").length;
  
  if (digitsLength <= 11) {
    return DOCUMENT_MASK(DocumentType.CPF);
  } else {
    return DOCUMENT_MASK(DocumentType.CNPJ);
  }
};

export const applyDocumentMask = (document: string | null | undefined) => {
  if (!document) return "";
  const digits = document.replace(/\D/g, "");
  
  if (!digits) return "";
  
  if (digits.length <= 11) {
    return DOCUMENT_MASK(DocumentType.CPF);
  } else {
    return DOCUMENT_MASK(DocumentType.CNPJ);
  }
};

export const formatWithMask = (value: string, mask: string) => {
  let result = "";
  let maskIndex = 0;
  let valueIndex = 0;

  while (maskIndex < mask.length && valueIndex < value.length) {
    if (mask[maskIndex] === "9") {
      result += value[valueIndex];
      valueIndex++;
    } else {
      result += mask[maskIndex];
    }
    maskIndex++;
  }

  return result;
};

export function formatCpfOrCnpj(document: string): string {
  const cleanedDocument = document.replace(/\D/g, "");

  if (cleanedDocument.length === 11) {
    return cleanedDocument.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (cleanedDocument.length === 14) {
    return cleanedDocument.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }

  return document;
}
